// Steps 4–6, and the waiting states between them. Each is drawn from the run
// as the server last reported it; the page polls and re-renders.
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { api, type MigrationCounts, type MigrationPhase, type MigrationRun } from "../../lib/api"
import { formatBytes, formatCount, formatHours, targetLabel } from "../../lib/migrations"
import { Button, ErrorNote, formatDateTime, useToast } from "../ui"
import { Bar, ItemList, Note, Stat, StepLayout } from "./parts"

/** Shown while the server reads the project or runs the preview. */
export function Working({ run, title, intro }: { run: MigrationRun; title: string; intro: string }) {
  const p = run.progress
  return (
    <StepLayout
      title={title}
      intro={intro}
      guide={{
        title: "What is happening",
        points: [
          "Moderniser is reading the project from the source. It only reads.",
          "You can leave this page. Come back from Admin → Migrations.",
        ],
      }}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="size-2 animate-pulse rounded-full bg-warning motion-reduce:animate-none" aria-hidden="true" />
          <span className="text-sm font-medium text-foreground">{p?.label ?? "Starting…"}</span>
          {p && p.total > 0 && (
            <span className="ml-auto font-mono text-xs text-muted tabular-nums">
              {formatCount(p.done)} of {formatCount(p.total)}
            </span>
          )}
        </div>
        <Bar percent={p && p.total ? (p.done / p.total) * 100 : 0} />
      </div>
    </StepLayout>
  )
}

function CountTiles({ counts }: { counts: MigrationCounts }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <Stat
        label="Items"
        value={formatCount(counts.items)}
        sub={`${formatCount(counts.epics)} epics, ${formatCount(counts.tasks)} tasks, ${formatCount(counts.subtasks)} sub-tasks`}
      />
      <Stat label="Comments" value={formatCount(counts.comments)} sub={`${formatCount(counts.mentions)} mentions kept`} />
      <Stat label="Attachments" value={formatCount(counts.attachments)} sub={formatBytes(counts.attachmentBytes)} />
      <Stat label="Time logged" value={formatHours(counts.timeMinutes)} sub={`${formatCount(counts.timeEntries)} entries`} />
      <Stat label="Sprints" value={formatCount(counts.sprints)} sub={`${formatCount(counts.tasksInSprint)} tasks in a sprint`} />
    </div>
  )
}

function Lists({
  warnings,
  skipped,
  problems = [],
}: {
  warnings: string[]
  skipped: { what: string; ref: string; reason: string }[]
  problems?: string[]
}) {
  return (
    <>
      {problems.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-danger">Must be fixed first ({problems.length})</h3>
          <ItemList items={problems.map((p) => ({ what: "problem", detail: p }))} />
        </div>
      )}
      {warnings.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-foreground">Warnings ({warnings.length})</h3>
          <ItemList items={warnings.map((w) => ({ what: "warning", detail: w }))} />
        </div>
      )}
      {skipped.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-foreground">Left out ({skipped.length})</h3>
          <ItemList items={skipped.slice(0, 50).map((s) => ({ what: s.what, detail: `${s.ref}: ${s.reason}` }))} />
          {skipped.length > 50 && <p className="text-xs text-faint">and {skipped.length - 50} more, listed in the report.</p>}
        </div>
      )}
    </>
  )
}

export function PreviewStep({ run, onChange }: { run: MigrationRun; onChange: (run: MigrationRun) => void }) {
  const preview = run.preview!
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const { counts, storage } = preview
  const after = storage.usedBytes + storage.addBytes
  const pct = storage.limitBytes ? (after / storage.limitBytes) * 100 : 0
  const blocked = preview.problems.length > 0

  const start = async () => {
    setBusy(true)
    setError(null)
    try {
      onChange((await api.post<{ run: MigrationRun }>(`/migrations/${run.id}/start`)).run)
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  const back = async () => {
    setError(null)
    try {
      // Re-saving the mapping as it is puts the run back into review.
      onChange((await api.patch<{ run: MigrationRun }>(`/migrations/${run.id}`, { mapping: run.mapping })).run)
    } catch (err) {
      setError(err)
    }
  }

  return (
    <StepLayout
      title="Preview"
      intro="This is exactly what the import will create. Nothing has been written yet."
      guide={{
        points: [
          "Runs the whole import without saving it, so the numbers are real.",
          "Problems that would stop the import appear here in red and must be fixed first.",
          "Warnings and left-out items do not stop it.",
        ],
        tip: "The import can take a while for large projects, mostly downloading attachments. You can leave the page once it starts.",
      }}
      footer={
        <>
          <Button type="button" variant="subtle" onClick={back}>
            Back to mapping
          </Button>
          <Button type="button" className="ml-auto" onClick={start} disabled={busy || blocked}>
            {busy ? "Starting…" : "Start import"}
          </Button>
        </>
      }
    >
      <ErrorNote error={error} />
      <CountTiles counts={counts} />
      {preview.reconciled ? (
        <Note tone="success">
          All {formatCount(counts.issues)} issues are accounted for: {formatCount(counts.epics)} epics,{" "}
          {formatCount(counts.tasks)} tasks, {formatCount(counts.subtasks)} sub-tasks, {formatCount(counts.skipped)} skipped.
        </Note>
      ) : (
        <Note tone="danger">Some issues were neither imported nor skipped. This usually means a parent loop in the source.</Note>
      )}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Storage</h3>
          <span className="ml-auto font-mono text-xs text-muted tabular-nums">
            {formatBytes(after)} of {formatBytes(storage.limitBytes)} after import
          </span>
        </div>
        <Bar percent={pct} tone={pct > 100 ? "danger" : pct > 85 ? "warning" : "success"} />
      </div>
      <Lists warnings={preview.warnings} skipped={preview.skipped} problems={preview.problems} />
    </StepLayout>
  )
}

const PHASES: { id: MigrationPhase; label: string }[] = [
  { id: "reading", label: "Reading issues from the source" },
  { id: "downloading", label: "Downloading attachments" },
  { id: "writing", label: "Writing tasks, comments and time" },
  { id: "uploading", label: "Uploading files" },
]

export function ImportStep({ run, onChange }: { run: MigrationRun; onChange: (run: MigrationRun) => void }) {
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const at = run.phase ? PHASES.findIndex((p) => p.id === run.phase) : -1
  const p = run.progress
  const overall = at < 0 ? 0 : ((at + (p && p.total ? p.done / p.total : 0)) / PHASES.length) * 100

  const cancel = async () => {
    setError(null)
    try {
      onChange((await api.post<{ run: MigrationRun }>(`/migrations/${run.id}/cancel`)).run)
    } catch (err) {
      setError(err)
    }
  }

  return (
    <StepLayout
      title={`Importing ${run.sourceProject.key} into ${targetLabel(run)}`}
      intro="You can leave this page. The import keeps running on the server and carries on after a restart."
      guide={{
        title: "What is happening",
        points: [
          "Files are downloaded from the source first, then tasks, comments and time are written, then files are uploaded.",
          "Nobody is notified and no emails are sent.",
        ],
        tip: `Started ${run.startedAt ? formatDateTime(run.startedAt) : "just now"} by ${run.createdByName}.`,
      }}
      footer={
        confirming ? (
          <>
            <span className="text-sm text-foreground">Stop the import after the current step?</span>
            <Button type="button" variant="danger" size="sm" onClick={cancel}>
              Stop import
            </Button>
            <Button type="button" variant="subtle" size="sm" onClick={() => setConfirming(false)}>
              Keep going
            </Button>
          </>
        ) : (
          <>
            <Button type="button" variant="danger" size="sm" onClick={() => setConfirming(true)}>
              Cancel import
            </Button>
            <span className="text-xs text-faint">
              Cancelling keeps what has been written so far. Running the migration again continues where it stopped.
            </span>
          </>
        )
      }
    >
      <ErrorNote error={error} />
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            {run.status === "queued" ? "Waiting to start…" : (p?.label ?? PHASES[Math.max(0, at)]!.label)}
          </span>
          {p && p.total > 0 && (
            <span className="ml-auto font-mono text-xs text-muted tabular-nums">
              {formatCount(p.done)} of {formatCount(p.total)}
            </span>
          )}
        </div>
        <Bar percent={overall} />
      </div>
      <ol className="flex flex-col">
        {PHASES.map((phase, i) => {
          const state = i < at ? "done" : i === at ? "running" : "pending"
          return (
            <li key={phase.id} className="flex items-center gap-3 border-t border-border py-3 text-sm first:border-t-0">
              <span
                className={`flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] ${
                  state === "done"
                    ? "bg-success-soft text-success"
                    : state === "running"
                      ? "border border-accent"
                      : "border border-border-strong text-faint"
                }`}
                aria-hidden="true"
              >
                {state === "done" ? "✓" : state === "running" ? <span className="size-2 animate-pulse rounded-full bg-warning motion-reduce:animate-none" /> : null}
              </span>
              <span className={state === "pending" ? "text-faint" : "text-foreground"}>{phase.label}</span>
              <span className="sr-only">{state === "done" ? "(done)" : state === "running" ? "(in progress)" : "(waiting)"}</span>
              {state === "running" && p && p.total > 0 && (
                <span className="ml-auto font-mono text-xs text-muted tabular-nums">
                  {formatCount(p.done)} of {formatCount(p.total)}
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </StepLayout>
  )
}

export function ReportStep({ run }: { run: MigrationRun }) {
  const navigate = useNavigate()
  const toast = useToast()
  const target = targetLabel(run)

  const downloadKeymap = async () => {
    try {
      const csv = await api.raw(`/migrations/${run.id}/keymap.csv`)
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }))
      const a = document.createElement("a")
      a.href = url
      a.download = `keymap-${run.sourceProject.key}-${target}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast("Could not download the key map. Try again.", "error")
    }
  }

  if (run.status !== "completed") {
    const cancelled = run.status === "cancelled"
    return (
      <StepLayout
        title={cancelled ? "Migration cancelled" : "Migration failed"}
        intro={
          cancelled
            ? "The import stopped. What had been written so far stays in the project."
            : "The import stopped before it finished. What had been written so far stays in the project."
        }
        guide={{
          title: "Good to know",
          points: [
            "The source token has been deleted.",
            "Starting again with the same project continues where this one stopped. It never duplicates tasks.",
          ],
        }}
        footer={
          <>
            <Button type="button" variant="subtle" onClick={() => navigate("/migrations")}>
              Back to migrations
            </Button>
            <Button type="button" className="ml-auto" onClick={() => navigate(`/migrations/new?source=${run.source}`)}>
              Start again
            </Button>
          </>
        }
      >
        {run.error && <Note tone="danger">{run.error}</Note>}
      </StepLayout>
    )
  }

  const summary = run.summary!
  const c = summary.counts
  const issuesLeft = summary.warnings.length + summary.skipped.length

  return (
    <StepLayout
      title={`Imported ${run.sourceProject.key} into ${target}`}
      intro={`${formatCount(c.epics)} epics, ${formatCount(c.tasks)} tasks, ${formatCount(c.subtasks)} sub-tasks, ${formatCount(c.comments)} comments, ${formatCount(c.attachments)} attachments and ${formatHours(c.timeMinutes)} are now in Moderniser.`}
      guide={{
        title: "Good to know",
        points: [
          "The source token has been deleted.",
          "Running the same migration again adds only what changed in the source since. It never duplicates tasks.",
        ],
      }}
      footer={
        <Button type="button" variant="subtle" className="ml-auto" onClick={() => navigate("/migrations")}>
          Back to migrations
        </Button>
      }
    >
      {issuesLeft > 0 ? (
        <Note tone="warning">
          Completed with {summary.warnings.length} warning{summary.warnings.length === 1 ? "" : "s"} and{" "}
          {summary.skipped.length} item{summary.skipped.length === 1 ? "" : "s"} left out. They are listed below.
        </Note>
      ) : (
        <Note tone="success">Completed with nothing left out.</Note>
      )}

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-foreground">Next steps</h3>
        <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {run.targetProject && (
            <NextStep
              title="Open the project"
              hint={`Board ${run.targetProject.key}, with its epics, tasks and sub-tasks as cards. Swimlanes group them by epic.`}
              action={
                <Link
                  to={`/projects/${run.targetProject.id}`}
                  className="rounded-md bg-accent px-2.5 py-1.5 text-xs font-medium text-accent-foreground hover:bg-accent/90"
                >
                  Open {run.targetProject.key}
                </Link>
              }
            />
          )}
          {summary.newPeople.length > 0 && (
            <NextStep
              title={`Activate ${summary.newPeople.length} new ${summary.newPeople.length === 1 ? "person" : "people"}`}
              hint={`${summary.newPeople
                .slice(0, 3)
                .map((p) => p.name)
                .join(", ")}${summary.newPeople.length > 3 ? ` and ${summary.newPeople.length - 3} more` : ""} ${summary.newPeople.length === 1 ? "was" : "were"} added as disabled accounts.`}
              action={
                <Button type="button" variant="ghost" size="sm" onClick={() => navigate("/employees")}>
                  Go to Employees
                </Button>
              }
            />
          )}
          <NextStep
            title="Key map"
            hint="Which source ID became which task. Each task also keeps its source ID in a field."
            action={
              <Button type="button" variant="ghost" size="sm" onClick={downloadKeymap}>
                Download CSV
              </Button>
            }
          />
        </div>
      </div>

      <CountTiles counts={c} />
      <Lists warnings={summary.warnings} skipped={summary.skipped} />
    </StepLayout>
  )
}

function NextStep({ title, hint, action }: { title: string; hint: string; action: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-48 flex-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted">{hint}</p>
      </div>
      {action}
    </div>
  )
}
